import { useCallback, useEffect, useRef, useState } from 'react'
import { clearDraftChunks, getDraftChunks, saveDraftChunk } from '../lib/indexedDb'
import { getSupportedRecordingMimeType } from '../services/audioService'

type RecorderState = 'idle' | 'recording' | 'paused' | 'stopped'

export const useRecorder = (lessonId: string | null) => {
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [level, setLevel] = useState(0)
  const [recoveredBlob, setRecoveredBlob] = useState<Blob | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const sequenceRef = useRef(0)
  const intervalRef = useRef<number | null>(null)
  const analyserFrameRef = useRef<number | null>(null)
  const startedAtRef = useRef(0)
  const pausedTotalRef = useRef(0)
  const pausedAtRef = useRef(0)

  const stopVisuals = () => {
    if (intervalRef.current) window.clearInterval(intervalRef.current)
    if (analyserFrameRef.current) cancelAnimationFrame(analyserFrameRef.current)
    intervalRef.current = null
    analyserFrameRef.current = null
    setLevel(0)
  }

  useEffect(() => {
    if (!lessonId) return
    getDraftChunks(lessonId).then((chunks) => {
      if (!chunks.length) {
        setRecoveredBlob(null)
        return
      }
      setRecoveredBlob(new Blob(chunks.map((chunk) => chunk.blob), { type: chunks[0].mimeType }))
    })
  }, [lessonId])

  useEffect(
    () => () => {
      stopVisuals()
      streamRef.current?.getTracks().forEach((track) => track.stop())
    },
    [],
  )

  const startLevelMeter = (stream: MediaStream) => {
    const context = new AudioContext()
    const analyser = context.createAnalyser()
    analyser.fftSize = 256
    context.createMediaStreamSource(stream).connect(analyser)
    const data = new Uint8Array(analyser.frequencyBinCount)
    const tick = () => {
      analyser.getByteFrequencyData(data)
      setLevel(Math.min(100, (data.reduce((sum, value) => sum + value, 0) / data.length) * 1.8))
      analyserFrameRef.current = requestAnimationFrame(tick)
    }
    tick()
  }

  const start = useCallback(async () => {
    if (!lessonId) throw new Error('Алдымен сабақ таңдаңыз.')
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      throw new Error('Бұл браузер MediaRecorder аудио жазуын қолдамайды.')
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
    const mimeType = getSupportedRecordingMimeType()
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
    await clearDraftChunks(lessonId)
    chunksRef.current = []
    sequenceRef.current = 0
    setRecoveredBlob(null)

    recorder.ondataavailable = async (event) => {
      if (!event.data.size) return
      chunksRef.current.push(event.data)
      const sequence = sequenceRef.current
      sequenceRef.current += 1
      await saveDraftChunk({
        id: `${lessonId}-${sequence}`,
        lessonId,
        sequence,
        mimeType: recorder.mimeType,
        blob: event.data,
        createdAt: Date.now(),
      })
    }
    recorderRef.current = recorder
    streamRef.current = stream
    recorder.start(5_000)
    startedAtRef.current = Date.now()
    pausedTotalRef.current = 0
    setElapsed(0)
    setState('recording')
    startLevelMeter(stream)
    intervalRef.current = window.setInterval(() => {
      const activePause = pausedAtRef.current ? Date.now() - pausedAtRef.current : 0
      setElapsed(Math.max(0, Date.now() - startedAtRef.current - pausedTotalRef.current - activePause))
    }, 500)
  }, [lessonId])

  const pause = () => {
    const recorder = recorderRef.current
    if (recorder?.state !== 'recording') return
    recorder.requestData()
    recorder.pause()
    pausedAtRef.current = Date.now()
    setState('paused')
  }

  const resume = () => {
    const recorder = recorderRef.current
    if (recorder?.state !== 'paused') return
    pausedTotalRef.current += Date.now() - pausedAtRef.current
    pausedAtRef.current = 0
    recorder.resume()
    setState('recording')
  }

  const stop = () =>
    new Promise<Blob>((resolve, reject) => {
      const recorder = recorderRef.current
      if (!recorder || recorder.state === 'inactive') {
        reject(new Error('Белсенді аудио жазба жоқ.'))
        return
      }
      recorder.onerror = () => reject(new Error('Аудио жазу кезінде браузер қатесі шықты.'))
      recorder.onstop = async () => {
        stopVisuals()
        streamRef.current?.getTracks().forEach((track) => track.stop())
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        setRecoveredBlob(blob)
        setState('stopped')
        resolve(blob)
      }
      recorder.requestData()
      recorder.stop()
    })

  const discardDraft = async () => {
    if (lessonId) await clearDraftChunks(lessonId)
    setRecoveredBlob(null)
    setState('idle')
    setElapsed(0)
  }

  return { state, elapsed, level, recoveredBlob, start, pause, resume, stop, discardDraft }
}
